import React, { memo, useCallback, useMemo, useState, type FC } from 'react';
import type { LayoutRectangle } from 'react-native';

import { Stack } from '@/components/layout';

import type {
  SelectContentProps,
  SelectItemProps,
  SelectProps,
  SelectTriggerProps,
} from '@/components/ui/select/types';
import type { SelectSheetContentProps } from '@/components/ui/select/select-sheet-content';
import { STRINGS } from '@/constants/strings';
import { SelectContent } from '@/components/ui/select/select-content';
import { SelectSheetContent } from '@/components/ui/select/select-sheet-content';
import { SelectContext, type SelectContextValue } from '@/components/ui/select/select-context';
import { SelectItem } from '@/components/ui/select/select-item';
import { SelectTrigger } from '@/components/ui/select/select-trigger';
import { useStyles } from '@/components/ui/select/styles';
import { Text } from '@/components/ui/text';

const SelectRoot: FC<SelectProps> = ({
  options,
  value,
  defaultValue = null,
  onValueChange,
  placeholder = STRINGS.common.selectDefaultPlaceholder,
  disabled = false,
  closeOnSelect = true,
  status = 'default',
  helperText,
  children,
  style,
}) => {
  const [internalValue, setInternalValue] = useState<string | null>(defaultValue);
  const [open, setOpen] = useState(false);
  const [triggerLayout, setTriggerLayout] = useState<LayoutRectangle | null>(null);
  const styles = useStyles();

  const isControlled = value !== undefined;
  const selectedValue = isControlled ? (value ?? null) : internalValue;

  const setValue = useCallback(
    (next: string) => {
      if (disabled) return;
      if (!isControlled) setInternalValue(next);
      onValueChange?.(next);
      if (closeOnSelect) setOpen(false);
    },
    [closeOnSelect, disabled, isControlled, onValueChange],
  );

  const ctx = useMemo<SelectContextValue>(
    () => ({
      disabled,
      open,
      setOpen,
      options,
      selectedValue,
      setValue,
      closeOnSelect,
      placeholder,
      status,
      triggerLayout,
      setTriggerLayout,
    }),
    [
      closeOnSelect,
      disabled,
      open,
      options,
      placeholder,
      selectedValue,
      setValue,
      status,
      triggerLayout,
      setTriggerLayout,
    ],
  );

  return (
    <SelectContext.Provider value={ctx}>
      <Stack style={[styles.root, style]}>
        {children}
        {helperText ? (
          <Text
            variant="Font Inter/Subtitle/Regular 11px"
            style={[styles.helperText, status === 'error' ? styles.helperTextError : undefined]}>
            {helperText}
          </Text>
        ) : null}
      </Stack>
    </SelectContext.Provider>
  );
};

type SelectCompoundComponent = FC<SelectProps> & {
  Trigger: FC<SelectTriggerProps>;
  Content: FC<SelectContentProps>;
  SheetContent: FC<SelectSheetContentProps>;
  Item: FC<SelectItemProps>;
};

export const Select = Object.assign(memo(SelectRoot), {
  Trigger: SelectTrigger,
  Content: SelectContent,
  SheetContent: SelectSheetContent,
  Item: SelectItem as FC<SelectItemProps>,
}) as SelectCompoundComponent;
